import React from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { styled } from '@mui/system';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { COLORS } from '../utils/COLORS';

const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000",
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
});

const Header = styled(Box)({
  width: "100%",
  padding: "16px 24px",
});

const HeaderContent = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
});

const MainContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  margin: "0 auto",
  padding: "40px 24px",
  flex: 1,
  [theme?.breakpoints?.down("md")]: {
    padding: "20px 16px",
  },
}));

const Section = styled(Paper)({
  backgroundColor: "#1F2937",
  borderRadius: "12px",
  padding: "32px",
  border: "none",
  marginBottom: "40px",
});

const SectionTitle = styled(Typography)({
  fontSize: "24px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "16px",
});

const SectionText = styled(Typography)({
  fontSize: "16px",
  lineHeight: "1.6",
  color: "#D1D5DB",
  marginBottom: "16px",
  fontWeight: "400",
});

const ContactForm = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#374151",
    borderRadius: "8px",
    color: "#FFFFFF",
    "& fieldset": {
      borderColor: "#4B5563",
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#10B981",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#9CA3AF",
    "&.Mui-focused": {
      color: "#10B981",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#FFFFFF",
  },
});

const SubmitButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "16px",
  fontWeight: "600",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#059669",
  },
});

const MapContainer = styled(Box)({
  width: "100%",
  height: "300px",
  backgroundColor: "#374151",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#9CA3AF",
  fontSize: "16px",
});

const Footer = styled(Box)({
  textAlign: "center",
  padding: "32px 24px",
  color: "#6B7280",
  fontSize: "14px",
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
});

const FooterLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const Bullet = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  userSelect: "none",
});

const ContactPage = () => {
  return (
    <PageContainer>
      {/* Header */}
      <Header>
        <HeaderContent>
          <Logo>
            <FavoriteIcon sx={{ fontSize: "20px" }} />
            PledgeFast
          </Logo>
        </HeaderContent>
      </Header>

      <MainContainer>
        {/* About Us Section */}
        <Section elevation={0}>
          <SectionTitle>About Us</SectionTitle>
          <SectionText>
            <strong>Mission:</strong> PledgeFast connects compassionate supporters with rescue organizations to save animals in urgent need. Our platform enables immediate pledging for at-risk pets while ensuring funds are only processed when rescues provide verified proof of successful placement.
          </SectionText>
          <SectionText>
            We believe every animal deserves a chance at life and work to bridge the gap between those who want to help and the rescues doing the vital work on the ground.
          </SectionText>
        </Section>

        {/* Contact Us Section */}
        <Section elevation={0}>
          <SectionTitle>Contact Us</SectionTitle>
          <SectionText>
            Have questions or want to get involved? We'd love to hear from you.
          </SectionText>
          
          <ContactForm>
            <StyledTextField
              label="Name"
              variant="outlined"
              fullWidth
            />
            <StyledTextField
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
            />
            <StyledTextField
              label="Subject"
              variant="outlined"
              fullWidth
            />
            <StyledTextField
              label="Message"
              variant="outlined"
              multiline
              rows={4}
              fullWidth
            />
            <SubmitButton
              startIcon={<EmailIcon />}
            >
              Send Message
            </SubmitButton>
          </ContactForm>
        </Section>

        {/* Map Section */}
        <Section elevation={0}>
          <SectionTitle>
            <LocationOnIcon sx={{ fontSize: "24px", marginRight: "8px", verticalAlign: "middle" }} />
            Our Location
          </SectionTitle>
          <SectionText>
            Based in Austin, Texas - serving rescue organizations nationwide.
          </SectionText>
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3445.2616443068636!2d-97.74306082464!3d30.26628757481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8644b599a0cc032f%3A0x5d9b464bd7cb59ed!2sDowntown%20Austin%2C%20Austin%2C%20TX!5e0!3m2!1sen!2sus!4v1704115200000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: "12px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </Section>
      </MainContainer>

      {/* Footer */}
      <Footer>
        <FooterLink>Login</FooterLink>
        <Bullet>•</Bullet>
        <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
          PledgeFast
        </Typography>
        <Bullet>•</Bullet>
        <FooterLink>Contact</FooterLink>
      </Footer>
    </PageContainer>
  );
};

export default ContactPage;